import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useRef, useState } from "react";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { tokens } from "../../../../../../../theme";
import apiService from "../../../../../../../service/Api";
import TestimonialsSelector from "./TestimonialsSelector";

interface Testimonial {
  id: number;
  author_name: string;
  author_description: string;
  company_name: string | null;
  rating: number;
  content: string;
  display_order: number;
}

interface TestimonialsSectionProps {
  onChange?: (ids: number[]) => void;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

export default function TestimonialsSection({
  onChange,
}: TestimonialsSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [allTestimonials, setAllTestimonials] = useState<Testimonial[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectorOpen, setSelectorOpen] = useState(false);

  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiService.get<
          Testimonial[] | { results?: Testimonial[] }
        >("/api/configurations/testimonials/");
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.results ?? [];
        setAllTestimonials(data);
        const autoSelected = data.slice(0, 3).map((t) => t.id);
        setSelectedIds(autoSelected);
        onChangeRef.current?.(autoSelected);
      } catch (err) {
        console.error("Failed to fetch testimonials:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSelectionChange = (ids: number[]) => {
    setSelectedIds(ids);
    onChange?.(ids);
  };

  const handleCardClick = (id: number) => {
    const isSelected = selectedIds.includes(id);
    
    if (!isSelected && selectedIds.length >= 3) return;

    const newSelection = isSelected
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];

    setSelectedIds(newSelection);
    onChange?.(newSelection);
  };

  const displayedTestimonials = allTestimonials.slice(0, 4);

  return (
    <Paper
      sx={{
        borderRadius: "14px",
        p: 2.5,
        boxShadow: 0,
        mb: 2,
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "15px",
              color: theme.palette.text.primary,
            }}
          >
            Testimonials
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}>
            {selectedIds.length}/3 selected
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => setSelectorOpen(true)}
          sx={{
            borderRadius: "20px",
            borderColor: colors.primary[500],
            color: colors.primary[500],
            textTransform: "none",
            fontWeight: 500,
            fontSize: "12px",
            px: 2,
            py: 0.5,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              bgcolor: theme.palette.primary.main + "15",
            },
          }}
        >
          View all
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: colors.primary[400] }} />
        </Box>
      ) : displayedTestimonials.length === 0 ? (
        <Typography
          sx={{
            fontSize: "13px",
            color: "text.secondary",
            textAlign: "center",
            py: 2,
          }}
        >
          No testimonials available.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {displayedTestimonials.map((t) => {
            const isSelected = selectedIds.includes(t.id);
            const isDisabled = selectedIds.length >= 3 && !isSelected;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={t.id}>
                <Box
                  onClick={() => handleCardClick(t.id)}
                  sx={{
                    height: "100%",
                    bgcolor: colors.primary[50],
                    borderRadius: "12px",
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative", 
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.6 : 1, 
                    border: isSelected
                      ? `2px solid ${colors.primary[400]}`
                      : `2px solid transparent`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: isDisabled ? "transparent" : colors.primary[300],
                    },
                  }}
                >
                  {/* Selected Checkmark */}
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "background.paper",
                        borderRadius: "50%",
                        width: 22,
                        height: 22,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleRounded
                        sx={{ fontSize: 22, color: colors.primary[400] }}
                      />
                    </Box>
                  )}

                  {/* Quote */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "36px",
                        lineHeight: 0.8,
                        color: colors.primary[800],
                        fontFamily: "serif",
                        mb: 1,
                      }}
                    >
                      “
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: colors.primary[800],
                        lineHeight: 1.6,
                        mb: 3,
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {t.content}
                    </Typography>
                  </Box>

                  {/* Author */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: "24px",
                      p: 0.6,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: colors.primary[500],
                        fontSize: "11px",
                        fontWeight: 600,
                        color: colors.primary[900],
                      }}
                    >
                      {getInitials(t.author_name)}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t.author_name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {t.author_description || t.company_name || "Client"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <TestimonialsSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        selectedIds={selectedIds}
        onChange={handleSelectionChange}
        testimonials={allTestimonials} 
      />
    </Paper>
  );
}